
import { Button } from "@/components/ui/button";
import { Eye, Download, Trash2, Copy } from "lucide-react";
import { extractOriginalFileName } from "@/utils/fileOperations";
import { useToast } from "@/hooks/use-toast";
import type { FileObject } from "@supabase/storage-js";

interface FileListItemProps {
  file: FileObject;
  isSelected: boolean;
  onClick: (file: FileObject) => void;
  onPreview: (file: FileObject) => void;
  onDownload: (file: FileObject) => void;
  onDelete?: (file: FileObject) => void;
  allowDelete?: boolean;
}

export const FileListItem = ({
  file,
  isSelected,
  onClick,
  onPreview,
  onDownload,
  onDelete,
  allowDelete = false,
}: FileListItemProps) => {
  const { toast } = useToast();

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) {
      return '🖼️';
    } else if (ext === 'pdf') {
      return '📄';
    } else if (['doc', 'docx'].includes(ext || '')) {
      return '📝';
    }
    return '📎';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const copyPathToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(file.name);
      toast({
        title: "Copied!",
        description: "File path copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy file path",
        variant: "destructive",
      });
    }
  };

  return (
    <div
      className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
        isSelected ? 'bg-blue-50 border-blue-200 border-l-4' : ''
      }`}
      onClick={() => onClick(file)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="text-xl flex-shrink-0">{getFileIcon(file.name)}</span>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">
              {extractOriginalFileName(file.name)}
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <span>
                {formatFileSize(file.metadata?.size || 0)} • 
                {new Date(file.updated_at || file.created_at).toLocaleDateString()}
              </span>
            </div>
            <div className="text-xs text-gray-400 flex items-center gap-1 mt-1">
              <span className="font-mono truncate">{file.name}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-gray-200"
                onClick={(e) => {
                  e.stopPropagation();
                  copyPathToClipboard();
                }}
                title="Copy file path"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onPreview(file);
            }}
            title="Preview"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(file);
            }}
            title="Download"
          >
            <Download className="h-4 w-4" />
          </Button>
          {allowDelete && onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(file);
              }}
              title="Delete"
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
